import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Clock, RotateCcw, Play, Pause, Volume2 } from 'lucide-react';

const ParagraphTestScreen = ({
  testData,
  answers,
  timer,
  relatedArticles,
  handleAnswerSelect,
  handleFillBlankInput,
  submitTest,
  resetTest,
}) => {
  // State for reading functionality
  const [state, setState] = useState({
    testStarted: false,
    speaking: false,
    currentWordIndex: -1,
    lastStoppedIndex: -1,
    speechRate: 1,
    highlightRate: 440,
    currentChunk: 0,
  });

  const [chunks, setChunks] = useState([]);
  const utteranceRef = useRef(null);
  const highlightIntervalRef = useRef(null);
  const isMountedRef = useRef(true);

  // Memoized values
  const actualWords = useMemo(() => {
    return testData?.passage ? testData.passage.split(/\s+/) : [];
  }, [testData?.passage]);

  // Safe state update function
  const updateState = useCallback((updates) => {
    if (isMountedRef.current) {
      setState(prevState => ({ ...prevState, ...updates }));
    }
  }, []);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (utteranceRef.current) {
      speechSynthesis.cancel();
    }
    if (highlightIntervalRef.current) {
      clearInterval(highlightIntervalRef.current);
      highlightIntervalRef.current = null;
    }
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Helper function to split text into manageable chunks
  const splitTextIntoChunks = useCallback((text, wordsPerChunk = 20) => {
    const words = text.split(/\s+/).filter(word => word.trim().length > 0);
    const chunks = [];
    
    for (let i = 0; i < words.length; i += wordsPerChunk) {
      chunks.push(words.slice(i, i + wordsPerChunk).join(' '));
    }
    
    return chunks;
  }, []);

  // Initialize chunks when testData changes
  useEffect(() => {
    if (testData?.passage && isMountedRef.current) {
      const newChunks = splitTextIntoChunks(testData.passage);
      setChunks(newChunks);
      updateState({
        currentChunk: 0,
        lastStoppedIndex: -1,
        currentWordIndex: -1
      });
    }
  }, [testData?.passage, splitTextIntoChunks, updateState]);

  // Function to speak a specific chunk
  const speakChunk = useCallback((chunkIndex) => {
    if (chunkIndex >= chunks.length || !chunks[chunkIndex] || !isMountedRef.current) {
      // Finished all chunks
      updateState({
        speaking: false,
        currentWordIndex: -1,
        currentChunk: 0,
        lastStoppedIndex: -1
      });
      if (highlightIntervalRef.current) {
        clearInterval(highlightIntervalRef.current);
        highlightIntervalRef.current = null;
      }
      return;
    }

    const utterance = new SpeechSynthesisUtterance(chunks[chunkIndex]);
    utteranceRef.current = utterance;
    utterance.rate = state.speechRate;

    // Calculate word positions for this chunk
    const wordsPerChunk = 20;
    const startWordIndex = chunkIndex * wordsPerChunk;
    let currentWordInChunk = 0;
    const wordsInThisChunk = Math.min(wordsPerChunk, actualWords.length - startWordIndex);

    // Clear any existing interval
    if (highlightIntervalRef.current) {
      clearInterval(highlightIntervalRef.current);
      highlightIntervalRef.current = null;
    }

    // Set initial word for immediate highlighting
    if (startWordIndex < actualWords.length && isMountedRef.current) {
      updateState({ currentWordIndex: startWordIndex });
      currentWordInChunk = 1;
    }

    // Highlight subsequent words as they're spoken
    highlightIntervalRef.current = setInterval(() => {
      if (!isMountedRef.current) {
        clearInterval(highlightIntervalRef.current);
        highlightIntervalRef.current = null;
        return;
      }
      
      if (currentWordInChunk < wordsInThisChunk && startWordIndex + currentWordInChunk < actualWords.length) {
        updateState({ currentWordIndex: startWordIndex + currentWordInChunk });
        currentWordInChunk++;
      } else {
        clearInterval(highlightIntervalRef.current);
        highlightIntervalRef.current = null;
      }
    }, state.highlightRate);

    utterance.onend = () => {
      if (!isMountedRef.current) return;
      
      if (highlightIntervalRef.current) {
        clearInterval(highlightIntervalRef.current);
        highlightIntervalRef.current = null;
      }
      
      const nextChunk = chunkIndex + 1;
      updateState({
        currentChunk: nextChunk,
        lastStoppedIndex: startWordIndex + wordsInThisChunk
      });
      
      // Continue to next chunk automatically if still speaking and more chunks exist
      if (nextChunk < chunks.length) {
        setTimeout(() => {
          if (isMountedRef.current) {
            speakChunk(nextChunk);
          }
        }, 100);
      } else {
        // Finished all chunks
        updateState({
          speaking: false,
          currentWordIndex: -1,
          lastStoppedIndex: -1,
          currentChunk: 0
        });
      }
    };

    utterance.onerror = (event) => {
      console.error('Speech error:', event);
      if (!isMountedRef.current) return;
      
      if (highlightIntervalRef.current) {
        clearInterval(highlightIntervalRef.current);
        highlightIntervalRef.current = null;
      }
      updateState({
        speaking: false,
        currentWordIndex: -1
      });
    };

    speechSynthesis.speak(utterance);
  }, [chunks, state.speechRate, state.highlightRate, actualWords.length, updateState]);

  // Main speech function
  const startSpeech = useCallback(() => {
    if (!speechSynthesis) {
      alert("Sorry, your browser doesn't support the Web Speech API");
      return;
    }

    if (state.speaking) {
      cleanup();
      updateState({ speaking: false });
      return;
    }

    // If we've finished all chunks, restart from beginning
    const chunkToStart = state.currentChunk >= chunks.length ? 0 : state.currentChunk;
    if (chunkToStart === 0) {
      updateState({ lastStoppedIndex: 0 });
    }

    updateState({ speaking: true });
    speakChunk(chunkToStart);
  }, [state.speaking, state.currentChunk, chunks.length, cleanup, updateState, speakChunk]);

  // Stop speech function
  const stopSpeech = useCallback(() => {
    cleanup();
    updateState({ speaking: false });
  }, [cleanup, updateState]);

  // Reset speech function
  const resetSpeech = useCallback(() => {
    cleanup();
    updateState({
      speaking: false,
      currentChunk: 0,
      currentWordIndex: -1,
      lastStoppedIndex: -1
    });
  }, [cleanup, updateState]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      cleanup();
    };
  }, [cleanup]);

  // Render passage with words for highlighting
  const renderPassage = useCallback(() => {
    if (!testData?.passage) return null;
    
    const words = testData.passage.split(/(\s+)/);
    let wordIndex = 0;
    
    return words.map((part, index) => {
      const isWord = !/^\s+$/.test(part) && part.trim().length > 0;
      
      if (!isWord) {
        return <span key={`space-${index}`}>{part}</span>;
      }
      
      const isHighlighted = state.currentWordIndex === wordIndex;
      const currentWordPosition = wordIndex;
      wordIndex++;
      
      return (
        <span
          key={`word-${index}-${currentWordPosition}`}
          style={{
            cursor: 'pointer',
            borderBottom: '1px dotted #ccc',
            ...(isHighlighted ? { 
              backgroundColor: '#FFEB3B',
              padding: '0 2px',
              borderRadius: '4px'
            } : {}),
          }}
          onMouseOver={(e) => {
            e.target.style.borderBottomColor = '#007bff';
          }}
          onMouseOut={(e) => {
            e.target.style.borderBottomColor = '#ccc';
          }}
        >
          {part}
        </span>
      );
    });
  }, [testData?.passage, state.currentWordIndex]);

  const renderQuestionText = (question) => {
    if (question.type === "mcq") {
      return (
        <span>
          {question.question}
          <span style={{ color: "#666" }}>
            {" ("}
            {question.options.map((option, index) => (
              <React.Fragment key={index}>
                <span
                  style={{
                    color: answers[question.id] === index ? "#2563eb" : "#666",
                    fontWeight: answers[question.id] === index ? "bold" : "normal",
                    cursor: "pointer",
                  }}
                  onClick={() => handleAnswerSelect(question.id, index)}
                >
                  {option}
                </span>
                {index < question.options.length - 1 ? "/" : ""}
              </React.Fragment>
            ))}
            {")"}
          </span>
        </span>
      );
    } else if (question.type === "fillBlank") {
      const parts = question.question.split("__________");
      return (
        <span>
          {parts[0]}
          <input
            type="text"
            value={answers[question.id]?.[0] || ""}
            onChange={(e) =>
              handleFillBlankInput(question.id, [e.target.value])
            }
            style={{
              border: "1px solid #ddd",
              padding: "3px 5px",
              margin: "0 2px",
              minWidth: "80px",
              display: "inline-block",
            }}
            placeholder="______"
          />
          {parts[1]}
          {question.blanks.length > 1 && (
            <>
              <input
                type="text"
                value={answers[question.id]?.[1] || ""}
                onChange={(e) =>
                  handleFillBlankInput(question.id, [
                    answers[question.id]?.[0] || "",
                    e.target.value,
                  ])
                }
                style={{
                  border: "1px solid #ddd",
                  padding: "3px 5px",
                  margin: "0 2px",
                  minWidth: "80px",
                  display: "inline-block",
                }}
                placeholder="______"
              />
              {parts[2]}
            </>
          )}
        </span>
      );
    }
    return null;
  };

  const handleStartTest = useCallback(() => {
    updateState({ testStarted: true });
  }, [updateState]);

  if (!state.testStarted) {
    return (
      <div style={{ maxWidth: "800px", margin: "0 auto" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <h2>Paragraph Format Test</h2>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Clock size={20} />
            <span>{formatTime(timer)}</span>
          </div>
        </div>

        {testData.passage && (
          <div
            style={{
              backgroundColor: "#f8f9fa",
              padding: "15px",
              borderRadius: "5px",
              marginBottom: "20px",
              borderLeft: "4px solid #2563eb",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
              <h3>Passage:</h3>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={startSpeech}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    padding: "5px 10px",
                    background: state.speaking ? "#f44336" : "#2563eb",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  {state.speaking ? <Pause size={16} /> : <Play size={16} />}
                  {state.speaking ? 'Stop Reading' : state.lastStoppedIndex > 0 ? 'Resume Reading' : 'Start Reading'}
                </button>
                {state.lastStoppedIndex > 0 && !state.speaking && (
                  <button
                    onClick={resetSpeech}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      padding: "5px 10px",
                      background: "#f0f0f0",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      cursor: "pointer",
                    }}
                  >
                    <RotateCcw size={16} />
                    Reset
                  </button>
                )}
              </div>
            </div>
            <div style={{ color: "#4b5563", lineHeight: "1.625" }}>
              {renderPassage()}
            </div>
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: "30px" }}>
          <button
            onClick={handleStartTest}
            style={{
              padding: "10px 20px",
              background: "#2563eb",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "16px",
            }}
          >
            Start Test
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: "800px", margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <h2>Paragraph Format Test</h2>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Clock size={20} />
          <span>{formatTime(timer)}</span>
          <button
            onClick={resetTest}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
              padding: "5px 10px",
              background: "#f0f0f0",
              border: "1px solid #ddd",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            <RotateCcw size={16} />
            Reset
          </button>
        </div>
      </div>

      {testData.passage && (
        <div
          style={{
            backgroundColor: "#f8f9fa",
            padding: "15px",
            borderRadius: "5px",
            marginBottom: "20px",
            borderLeft: "4px solid #2563eb",
          }}
        >
          <h3>Passage:</h3>
          <div style={{ color: "#4b5563", lineHeight: "1.625" }}>
            {renderPassage()}
          </div>
        </div>
      )}

      <div
        style={{
          backgroundColor: "white",
          padding: "20px",
          borderRadius: "5px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          lineHeight: "1.6",
        }}
      >
        {testData.questions.map((question, index) => (
          <React.Fragment key={question.id}>
            {renderQuestionText(question)}
            {index < testData.questions.length - 1 ? ". " : "."}
          </React.Fragment>
        ))}
      </div>

      <div style={{ textAlign: "center", marginTop: "30px" }}>
        <button
          onClick={submitTest}
          style={{
            padding: "10px 20px",
            background: "#2563eb",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "16px",
          }}
        >
          Submit Test
        </button>
      </div>
    </div>
  );
};

export default ParagraphTestScreen;